import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Plus, Users, Phone, Mail, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import AddTenantModal from '../components/modals/AddTenantModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState({});
  const [properties, setProperties] = useState({});
  const [leases, setLeases] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tenantsRes, usersRes, leasesRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/tenants`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/leases`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setTenants(tenantsRes.data);
      
      // Create user lookup map
      const userMap = {};
      usersRes.data.forEach(user => {
        userMap[user.id] = user;
      });
      setUsers(userMap);
      
      // Create lease lookup map
      const leaseMap = {};
      leasesRes.data.forEach(lease => {
        leaseMap[lease.id] = lease;
      });
      setLeases(leaseMap);
      
      // Create property lookup map
      const propertyMap = {};
      propertiesRes.data.forEach(property => {
        propertyMap[property.id] = property;
      });
      setProperties(propertyMap);
    } catch (error) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="tenants-page" className="max-w-7xl mx-auto animate-fade-in">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Tenants</h1>
            <p className="text-gray-600">Manage tenant accounts and profiles</p>
          </div>
          <Button data-testid="add-tenant-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4" />
            Add Tenant
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : tenants.length === 0 ? (
          <Card data-testid="empty-state">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <Users className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">No tenants yet</p>
              <Button data-testid="add-first-tenant-btn" className="gap-2" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4" />
                Add Your First Tenant
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tenants.map((tenant) => {
              const user = users[tenant.user_id] || {};
              const lease = leases[tenant.current_lease_id] || null;
              const property = lease ? properties[lease.property_id] : null;
              
              return (
                <Card key={tenant.id} data-testid={`tenant-card-${tenant.id}`} className="card-hover">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.name?.charAt(0) || 'T'}
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-gray-900">{user.name || 'Tenant'}</div>
                        {tenant.current_lease_id && (
                          <span className="badge badge-success text-xs">Active Lease</span>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Property Address - MOST IMPORTANT */}
                    {property && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-xs text-blue-600 font-semibold mb-1">Current Property</p>
                        <p className="text-sm font-medium text-gray-900">
                          {property.address}
                        </p>
                        <p className="text-xs text-gray-600">
                          {property.suburb}, {property.state} {property.postcode}
                        </p>
                      </div>
                    )}
                    
                    {user.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700 truncate">{user.email}</span>
                      </div>
                    )}
                    {user.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700">{user.phone}</span>
                      </div>
                    )}
                    {tenant.employer && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700">{tenant.employer}</span>
                      </div>
                    )}
                    {tenant.emergency_contact && (
                      <div className="pt-3 border-t">
                        <p className="text-xs text-gray-500 mb-1">Emergency Contact</p>
                        <p className="text-sm font-medium text-gray-900">{tenant.emergency_contact}</p>
                        {tenant.emergency_phone && (
                          <p className="text-sm text-gray-600">{tenant.emergency_phone}</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <AddTenantModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadTenants}
        />
      </div>
    </Layout>
  );
}